import { getOr, isEqual, isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useSingleHiddenFields } from '../../../../hooks/useHiddenSingleFields';
import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { getAllDropdownLists } from '../../../admin/dropdown-lists/store/selectors';
import { CollapseButton } from '../../../shared/button/CollapseButton';
import { DeleteButton } from '../../../shared/button/DeleteButton';
import { DuplicateButton } from '../../../shared/button/DuplicateButton';
import { PlusButton } from '../../../shared/button/PlusButton';
import styles from '../../../shared/datasets/SharedStyling.module.scss';
import { TableInstanceScrollable } from '../../../shared/scrollable/TableInstanceScrollable';
import { AgencyDatasetFieldType, DatasetFieldType, HiddenFields, TableDatasetFieldType } from '../../store';
import { addCollapsedColumn, addOpenFieldSection, addTableDatasetRow, AnnexInstance, AnnexInstanceField, clearTableInstanceFilters, duplicateTableDatasetRow, getAnnexDefinitionFieldIds, getOpenFieldsAndSections, getTableInstanceFilters, OpenFieldSection, removeCollapsedColumn, removeOpenFieldSection, removeTableDatasetRow, removeTableInstanceFieldFilter, setTableInstanceFieldFilter, SingleInstanceField, TableDatasetInstance } from '../store';
import { InstanceTableFilterType } from '../store/filterTypes';
import { checkTableCellMatchesFilter } from './filters/utils';
import { TableCell } from './TableCell';
import { TableHeader } from './TableHeader';

interface TableInstanceProps {
    datasetInstance: TableDatasetInstance | AnnexInstance;
    modalInstance?: boolean;
    isEditing: boolean;
    parentFieldId: string;
    isUpdating: boolean;
    instanceExecutedDateMatchesParent: boolean;
    hiddenFields?: HiddenFields;
    datasetHidden?: boolean;
    isMLCorrection?: boolean;
}

export const TableInstance: React.FC<TableInstanceProps> = ({ datasetInstance, isEditing, isUpdating, modalInstance, parentFieldId, hiddenFields, datasetHidden, instanceExecutedDateMatchesParent, isMLCorrection }) => {
    const dispatch = useAppDispatch();
    const [collapsedRows, setCollapsedRows] = useState<string[]>([]);

    const annexFieldIds = useAppSelector(getAnnexDefinitionFieldIds);
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const datasetId = useMemo(() => isNull(datasetInstance.annexDefinitionId) ? datasetInstance.datasetId! : datasetInstance.annexDefinitionId, [datasetInstance]);
    const tableFilters = useAppSelector(getTableInstanceFilters(datasetId, parentFieldId));
    const allDropdownLists = useAppSelector(getAllDropdownLists);
    const getFilterDropdownOptions = useCallback((listId: string | undefined | null) => (allDropdownLists.find(({ dropdownListId }) => dropdownListId.toString() === listId)?.options || []).map(option => ({ value: option, label: capitaliseStartLetter(option) })), [allDropdownLists]);

    const removeRow = (rowId: string) => dispatch(removeTableDatasetRow(datasetId, parentFieldId, rowId, modalInstance));
    const addRow = () => dispatch(addTableDatasetRow(datasetId, parentFieldId, modalInstance));
    const duplicateRow = (rowId: string) => dispatch(duplicateTableDatasetRow(datasetId, parentFieldId, rowId, modalInstance));

    const getRowIsCollapsed = useCallback((rowId: string) => collapsedRows.includes(rowId), [collapsedRows]);
    const collapseRow = useCallback((rowId: string) => {
        const updatedCollapsedRows = getRowIsCollapsed(rowId) ? collapsedRows.filter(collapsedRow => collapsedRow !== rowId) : [...collapsedRows, rowId];
        setCollapsedRows(updatedCollapsedRows);
    }, [getRowIsCollapsed, collapsedRows]);

    const singleRow = Object.values(datasetInstance.datasetFields).length === 1;
    const { fieldsUpdated, datasetRows, datasetFields } = datasetInstance;

    const sortedDatasetRows = useMemo(() => datasetRows.reduce((acc: [string, SingleInstanceField[] | AnnexInstanceField[]][], cur) => {
        const orderedRow = Object.entries(datasetFields).find(([rowId]) => rowId === cur);
        if (orderedRow) {
            acc.push(orderedRow);
        }
        return acc;
    }, []), [datasetRows, datasetFields]);

    const addRowVisible = useMemo(() => isEditing || isUpdating, [isEditing, isUpdating]);

    const getSectionOpen = useCallback((fieldSection: OpenFieldSection) => openFieldsAndSections.some(openSection => isEqual(openSection, fieldSection)), [openFieldsAndSections]);

    const toggleSection = useCallback((fieldSection: OpenFieldSection) => {
        const sectionOpen = openFieldsAndSections.find(openSection => isEqual(openSection, fieldSection));
        sectionOpen ? dispatch(removeOpenFieldSection(sectionOpen)) : dispatch(addOpenFieldSection(fieldSection));
    }, [dispatch, openFieldsAndSections]);

    const collapsedColumns = useMemo(() => datasetInstance.collapsedColumns || [], [datasetInstance.collapsedColumns]);

    const getColumnCollapsed = useCallback((fieldId: string) => collapsedColumns.some(id => isEqual(id, fieldId)), [collapsedColumns]);
    const toggleCollapseColumn = useCallback((fieldId: string) => {
        const columnIsCollapsed = collapsedColumns.find(id => isEqual(id, fieldId));
        columnIsCollapsed ? dispatch(removeCollapsedColumn(datasetId, parentFieldId, fieldId, modalInstance)) : dispatch(addCollapsedColumn(datasetId, parentFieldId, fieldId, modalInstance));
    }, [dispatch, collapsedColumns, datasetId, parentFieldId, modalInstance]);

    const currentHiddenDatasetFields = useMemo(() => hiddenFields && hiddenFields[datasetInstance.datasetId!] || [], [datasetInstance, hiddenFields]);

    const hiddenSingleFields = useSingleHiddenFields(currentHiddenDatasetFields);

    const columnIsHidden = useCallback((id: string) => datasetHidden || !!hiddenSingleFields.find(({ fieldId }) => fieldId === id), [hiddenSingleFields, datasetHidden]);

    const updateTableFilter = useCallback((fieldId: string, type: DatasetFieldType, filterValue: InstanceTableFilterType) => dispatch(setTableInstanceFieldFilter(datasetId, parentFieldId, fieldId, filterValue, type)), [dispatch, datasetId, parentFieldId]);
    const removeTableFilter = useCallback((fieldId: string) => dispatch(removeTableInstanceFieldFilter(datasetId, parentFieldId, fieldId)), [dispatch, datasetId, parentFieldId]);
    const clearAllFilters = useCallback(() => dispatch(clearTableInstanceFilters(datasetId, parentFieldId)), [dispatch, datasetId, parentFieldId]);

    const getRowIsVisible = useCallback((row: SingleInstanceField[] | AnnexInstanceField[]) => [...row].every(rowCell => {
        const cellFilter = tableFilters.find(({ fieldId }) => fieldId === rowCell.id);
        if (isUndefined(cellFilter)) {
            return true;
        }
        return checkTableCellMatchesFilter(rowCell, cellFilter);
    }), [tableFilters]);

    return (
        <div className={styles.tableInstanceWrapper} data-testid='table-dataset-instance-wrapper' style={{ height: '100%' }}>
            <TableInstanceScrollable collapsedRows={collapsedRows} filtersApplied={tableFilters}>
                <div className={styles.tableHeader}>
                    <div className={styles.collapseCell} />
                    {Object.values(datasetInstance.datasetFields)[0].map((field: SingleInstanceField | AnnexInstanceField) => {
                        const { label, description, refLabel, settings, id, type } = field;
                        const columnCollapsed = getColumnCollapsed(id!);
                        const columnHidden = columnIsHidden(id!);
                        const filterDropdownOptions = getFilterDropdownOptions(settings.dropdownLinked);
                        if (columnHidden) {
                            return null;
                        }
                        return (
                            <TableHeader
                                key={id!}
                                label={label}
                                description={description}
                                refLabel={refLabel}
                                showRef={settings.showRef}
                                id={id!}
                                type={type as TableDatasetFieldType | AgencyDatasetFieldType}
                                aliasLabel={getOr(undefined, 'aliasLabel', field)}
                                columnCollapsed={columnCollapsed}
                                toggleCollapseColumn={toggleCollapseColumn}
                                highlight={settings.highlight}
                                filters={tableFilters}
                                updateTableFilter={updateTableFilter}
                                removeTableFilter={removeTableFilter}
                                filterDropdownOptions={filterDropdownOptions}
                            />
                        );
                    })}
                    <div className={styles.actionRowHeader} />
                    <div className={styles.actionRowHeader} />
                </div>
                <div className={styles.tableBody} style={{ height: 'calc(100% - 50px)' }}>
                    {sortedDatasetRows.map(([rowId, row]) => {
                        const rowCollapsed = getRowIsCollapsed(rowId);
                        const rowVisible = getRowIsVisible(row);
                        if (!rowVisible) {
                            return null;
                        }
                        return (
                            <div className={styles.tableRow} key={rowId}>
                                <div className={styles.collapseRowCell}>
                                    <CollapseButton onClick={() => collapseRow(rowId)} isCollapsed={rowCollapsed} fontSize={14} />
                                </div>
                                {row.map((field: SingleInstanceField | AnnexInstanceField, index: number) => {
                                    const columnCollapsed = getColumnCollapsed(field.id!);
                                    const columnHidden = columnIsHidden(field.id!);
                                    if (columnHidden) {
                                        return null;
                                    }
                                    return (
                                        <TableCell
                                            key={field.id!}
                                            field={field}
                                            index={index}
                                            isEditing={isEditing}
                                            isUpdating={isUpdating}
                                            rowId={rowId}
                                            fieldsUpdated={fieldsUpdated}
                                            annexFieldIds={annexFieldIds}
                                            isAnnexInstance={!!datasetInstance.annexDefinitionId}
                                            datasetId={datasetId}
                                            modalInstance={modalInstance}
                                            toggleSection={toggleSection}
                                            getSectionOpen={getSectionOpen}
                                            parentFieldId={parentFieldId}
                                            columnCollapsed={columnCollapsed}
                                            rowCollapsed={rowCollapsed}
                                            instanceExecutedDateMatchesParent={instanceExecutedDateMatchesParent}
                                            isMLCorrection={isMLCorrection}
                                        />
                                    );
                                })}
                                <div className={styles.actionRowCell}>
                                    {isEditing && <DuplicateButton onClick={() => duplicateRow(rowId)} disabled={!isEditing} />}
                                </div>
                                <div className={styles.actionRowCell}>
                                    {!singleRow && isEditing && <DeleteButton onClick={() => removeRow(rowId)} disabled={!isEditing} />}
                                </div>
                            </div>
                        );
                    }
                    )}
                    {(addRowVisible || tableFilters.length > 0) &&
                        <div className={styles.addTableRow}>
                            {tableFilters.length > 0 && <button className={styles.clearAllFilters} onClick={clearAllFilters} data-testid='table-instance-clear-filters-button'>Clear All Filters</button>}
                            {addRowVisible && <PlusButton onClick={addRow} fontSize={20} />}
                        </div>
                    }
                </div>
            </TableInstanceScrollable>
        </div>
    );
};
